"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { navItems } from "@/lib/config";
import type { NavItem } from "@/types";

function NavLink({
  item,
  isActive,
  isMobile = false,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  isMobile?: boolean;
  onClick?: () => void;
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (item.children) {
    return (
      <div
        ref={dropdownRef}
        className="relative group"
        onMouseEnter={() => !isMobile && setIsDropdownOpen(true)}
        onMouseLeave={() => !isMobile && setIsDropdownOpen(false)}
        data-oid="ium8v57"
      >
        <button
          onClick={() => isMobile && setIsDropdownOpen(!isDropdownOpen)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsDropdownOpen(!isDropdownOpen);
            } else if (e.key === "Escape") {
              setIsDropdownOpen(false);
            }
          }}
          className={`
            flex items-center gap-1 text-base font-semibold tracking-wide transition-colors
            ${isActive ? "text-white" : "text-white/90 hover:text-white"}
          `}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
          aria-controls={`${item.title.toLowerCase()}-dropdown`}
          data-oid="cko7j3w"
        >
          {item.title}
          <svg
            className={`h-4 w-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
            data-oid=".lao1yd"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
              data-oid="b3hf3al"
            />
          </svg>
        </button>
        <div
          id={`${item.title.toLowerCase()}-dropdown`}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby={`${item.title.toLowerCase()}-button`}
          className={`
            ${
              isMobile
                ? "mt-2 ml-4 space-y-2"
                : `absolute top-full left-1/2 -translate-x-1/2 mt-1 w-48 rounded-md bg-white py-2 shadow-lg
                 opacity-0 invisible group-hover:opacity-100 group-hover:visible
                 transition-all duration-200 ease-in-out z-50`
            }
            ${isDropdownOpen ? "opacity-100 visible" : ""}
          `}
          data-oid="ohwroix"
        >
          {item.children.map((child, index) => (
            <Link
              key={child.href}
              href={child.href}
              onClick={() => {
                setIsDropdownOpen(false);
                onClick?.();
              }}
              role="menuitem"
              tabIndex={isDropdownOpen ? 0 : -1}
              className={`
                block px-4 py-2 text-sm transition-colors
                ${
                  isMobile
                    ? "text-white/90 hover:text-white"
                    : "text-gray-800 hover:bg-gray-100"
                }
              `}
              data-oid="6lojx:_"
            >
              {child.title}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Link
      href={item.href}
      onClick={onClick}
      className={`
        relative text-base font-semibold tracking-wide transition-colors
        ${
          isActive
            ? "text-white before:scale-x-100"
            : "text-white/90 hover:text-white before:scale-x-0 hover:before:scale-x-100"
        }
        ${
          !isMobile
            ? `
          before:absolute before:-bottom-1 before:left-0 before:h-0.5 before:w-full 
          before:origin-left before:bg-white before:transition-transform before:duration-200
        `
            : ""
        }
      `}
      aria-current={isActive ? "page" : undefined}
      data-oid="ffrpp21"
    >
      {item.title}
    </Link>
  );
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-white/10 bg-brand-blue"
      role="banner"
      data-oid="vfo3p2t"
    >
      <div
        className="container mx-auto flex h-16 items-center justify-between px-4"
        data-oid="4r7kgw8"
      >
        {/* Logo */}
        <Link
          href="/"
          className="flex items-center space-x-3 transition-colors hover:opacity-90"
          aria-label="Moya Fertility Home"
          data-oid="00dn7yt"
        >
          <div
            className="relative w-[40px] h-[40px] rounded-full overflow-hidden bg-white flex items-center justify-center"
            data-oid="n1_j9dp"
          >
            <img
              src="/images/Moya%20Logo.jpg"
              alt="Moya Fertility Logo"
              width={40}
              height={40}
              className="object-cover w-full h-full rounded-full"
              style={{ display: 'block' }}
            />
          </div>
          <span className="text-xl font-bold text-white" data-oid=".0_n8e_">
            Moya Fertility
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav
          className="hidden md:flex items-center space-x-8"
          data-oid="bn_4u2f"
        >
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              item={item}
              isActive={pathname === item.href}
              data-oid="ge03scd"
            />
          ))}
        </nav>

        {/* Contact Us Button */}
        <Link
          href="/contact"
          className="hidden md:inline-flex items-center px-6 py-2 text-sm font-semibold text-brand-blue bg-white rounded-full transition-colors hover:bg-white/90"
          data-oid="44l.blh"
        >
          Contact Us
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="block lg:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          data-oid="enz89-0"
        >
          <svg
            className="h-6 w-6 text-white"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
            data-oid="6.8tvlx"
          >
            {isMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" data-oid="3z_235-" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" data-oid="jx_rudg" />
            )}
          </svg>
        </button>

        {/* Mobile Navigation */}
        <div
          ref={menuRef}
          className={`
            fixed inset-y-0 right-0 w-64 bg-brand-blue p-6 shadow-lg transform transition-transform duration-200 ease-in-out
            ${isMenuOpen ? "translate-x-0" : "translate-x-full"}
          `}
          aria-hidden={!isMenuOpen}
          data-oid="qii6fbd"
        >
          <nav className="flex flex-col space-y-4" data-oid="zg1lsfc">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                item={item}
                isActive={pathname === item.href}
                isMobile={true}
                onClick={() => setIsMenuOpen(false)}
                data-oid="x.pswu8"
              />
            ))}
            {/* Mobile Contact Us Button */}
            <Link
              href="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="mt-4 inline-flex items-center justify-center px-6 py-2 text-sm font-semibold text-brand-blue bg-white rounded-full transition-colors hover:bg-white/90"
              data-oid="90._ax0"
            >
              Contact Us
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
}
